import { Component, computed, inject, signal } from '@angular/core';
import { DateRange, MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';

import { DefferedDialogComponent } from '@shared/abstractions/modal';
import { GenericButtonComponent } from '@shared/ui';
import { NotificationService } from '@core/services';
import { DateUtil } from '@core/utils';
import { AttachDeviceWidthDirective } from '@core/directives';
import { dateRangeFromOption, dateRangeToOption, DEFAULT_DATE_RANGE } from '../date.util';
import { PeriodOption } from '../date.model';

@Component({
  selector: 'fc-calendar-dialog',
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.scss',
  imports: [
    AttachDeviceWidthDirective,
    GenericButtonComponent,
    MatDatepickerModule,
  ],
})
export class CalendarDialogComponent extends DefferedDialogComponent<PeriodOption> {
  readonly DEFAULT_DATE_RANGE = DEFAULT_DATE_RANGE;
  private notificationService = inject(NotificationService);
  protected initialOption?: PeriodOption = inject(MAT_DIALOG_DATA);

  protected dateRange = signal<DateRange<Date | null>>(dateRangeFromOption(this.initialOption));

  readonly calendarFromStartAt = computed(() => this.dateRange().start || DEFAULT_DATE_RANGE.start);
  readonly calendarToStartAt = computed(() => {
    const d = this.dateRange();
    const start = (d.start || DEFAULT_DATE_RANGE.start) as Date;
    const end = (d.end || DEFAULT_DATE_RANGE.end) as Date;

    return start.getMonth() !== end.getMonth() ? end : new DateUtil(end).add(1, 'month').toDate();
  });

  setDate(date: Date): void {
    const d = this.dateRange();

    let startDate: Date | null = null;
    let endDate: Date | null = null;

    if (!d.start || (d.start && d.end)) {
      startDate = date;
      endDate = null;
    } else {
      if (date < d.start) {
        startDate = date;
        endDate = d.start;
      } else {
        startDate = d.start;
        endDate = date;
      }
    }

    this.dateRange.set(new DateRange(
      startDate ? new DateUtil(startDate).startOfDay().toDate() : null,
      endDate ? new DateUtil(endDate).endOfDay().toDate() : null,
    ));
  }

  submit(range: DateRange<Date | null>): void {
    if (!this.isCompleteRange(range)) {
      this.notificationService.showError('Invalid date range');
      return;
    }

    const opt = dateRangeToOption(range);
    this.submitFormData(opt);
  }

  private isCompleteRange(range: DateRange<Date | null>): range is DateRange<Date> {
    return range.start !== null && range.end !== null;
  }
}
